import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Card, Col, Form, Modal, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import {
    getLeccionesProyecto,
    postLeccionProyecto,
    putLeccionProyecto,
    deleteLeccionProyecto,
} from "../../api";

export const CATEGORIAS_LECCION = ["Técnica", "Gestión", "Equipo", "Proceso", "Otra"];

export const TIPOS_LECCION = {
    positiva: { label: "Positiva", variant: "success" },
    negativa: { label: "Negativa", variant: "danger" },
    mejora: { label: "Mejora", variant: "info" },
};

/**
 * Normaliza cualquier representación almacenada (array | objeto legado | texto)
 * a un array de lecciones. Réplica en cliente de api/utils/leccion-aprendida-utils.js.
 */
export function normalizarLecciones(valor) {
    if (!valor) return [];

    let data = valor;
    if (typeof data === "string") {
        const texto = data.trim();
        if (!texto) return [];
        try {
            data = JSON.parse(texto);
        } catch (e) {
            return [{ id: "legacy", descripcion: data, titulo: data.slice(0, 80), categoria: "Otra", tipo: "mejora", fecha: null, autorNombre: "" }];
        }
    }

    const normalizarUna = (l) => {
        const src = l && typeof l === "object" ? l : {};
        return {
            id: src.id ? String(src.id) : `tmp_${Math.random().toString(36).slice(2, 8)}`,
            titulo: src.titulo || "",
            descripcion: src.descripcion || "",
            categoria: CATEGORIAS_LECCION.includes(src.categoria) ? src.categoria : "Otra",
            tipo: TIPOS_LECCION[src.tipo] ? src.tipo : "mejora",
            fecha: src.fecha || null,
            autorNombre: src.autorNombre || "",
            creadoEn: src.creadoEn || null,
        };
    };

    if (Array.isArray(data)) return data.map(normalizarUna);
    if (typeof data === "object") {
        return [normalizarUna({ descripcion: data.descripcion || data.texto || "", fecha: data.fecha || data.fechaCierre || null })];
    }
    return [];
}

/**
 * Convierte las lecciones a texto plano para informes / volcado de IA.
 */
export function formatLeccionesTexto(valor) {
    const lista = normalizarLecciones(valor);
    if (lista.length === 0) return "";
    return lista
        .map((l, i) => {
            const partes = [];
            partes.push(`${i + 1}. ${l.titulo || "(Sin título)"}`);
            if (l.categoria && l.categoria !== "Otra") partes.push(`Categoría: ${l.categoria}`);
            const t = TIPOS_LECCION[l.tipo];
            if (t) partes.push(`Tipo: ${t.label}`);
            if (l.fecha) partes.push(`Fecha: ${moment(l.fecha).format("DD/MM/YYYY")}`);
            if (l.autorNombre) partes.push(`Autor: ${l.autorNombre}`);
            let texto = partes.join(" · ");
            if (l.descripcion) texto += `\n${l.descripcion}`;
            return texto;
        })
        .join("\n\n");
}

const FORM_VACIO = () => ({
    titulo: "",
    descripcion: "",
    categoria: "Otra",
    tipo: "mejora",
    fecha: moment().format("YYYY-MM-DD"),
});

/**
 * Módulo de Lecciones Aprendidas (Punto 14).
 * Disponible durante todo el ciclo de vida del proyecto (no solo al cerrar).
 *
 * @param {number|string} proyectoId
 * @param {*} lecciones          Valor inicial (JSONB del proyecto); se refresca vía API.
 * @param {boolean} puedeGestionar
 * @param {(lista: Array) => void} [onChange] Notifica la lista actualizada al padre.
 */
function LeccionesAprendidas({ proyectoId, lecciones, puedeGestionar = true, onChange }) {
    const [items, setItems] = useState(() => normalizarLecciones(lecciones));
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(FORM_VACIO());
    const [deleteTarget, setDeleteTarget] = useState(null);

    const onChangeRef = useRef(onChange);
    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    const sincronizarPadre = useCallback((lista) => {
        if (onChangeRef.current) onChangeRef.current(lista);
    }, []);

    // Carga desde la API al cambiar de proyecto.
    useEffect(() => {
        if (!proyectoId) return undefined;
        let activo = true;
        setLoading(true);
        (async () => {
            try {
                const res = await getLeccionesProyecto(proyectoId);
                if (activo && res.data && res.data.success) {
                    const lista = normalizarLecciones(res.data.data);
                    setItems(lista);
                    sincronizarPadre(lista);
                }
            } catch (e) {
                // Sin permiso de ver: conservar el valor que venga del proyecto.
            } finally {
                if (activo) setLoading(false);
            }
        })();
        return () => {
            activo = false;
        };
    }, [proyectoId, sincronizarPadre]);

    // Refresca cuando el proyecto cambia su campo lecciones_aprendidas (p. ej. carga inicial).
    useEffect(() => {
        setItems((prev) => {
            const normalizado = normalizarLecciones(lecciones);
            return normalizado.length > 0 ? normalizado : prev;
        });
    }, [lecciones]);

    const abrirNueva = () => {
        setEditingId(null);
        setForm(FORM_VACIO());
        setShowForm(true);
    };

    const abrirEditar = (leccion) => {
        setEditingId(leccion.id);
        setForm({
            titulo: leccion.titulo || "",
            descripcion: leccion.descripcion || "",
            categoria: leccion.categoria || "Otra",
            tipo: leccion.tipo || "mejora",
            fecha: leccion.fecha ? moment(leccion.fecha).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"),
        });
        setShowForm(true);
    };

    const guardar = async () => {
        if (!form.titulo.trim() && !form.descripcion.trim()) {
            toast.warn("Indique un título o una descripción.");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                titulo: form.titulo.trim(),
                descripcion: form.descripcion.trim(),
                categoria: form.categoria,
                tipo: form.tipo,
                fecha: form.fecha || null,
            };
            const res = editingId
                ? await putLeccionProyecto(proyectoId, editingId, payload)
                : await postLeccionProyecto(proyectoId, payload);

            if (res.data && res.data.success) {
                const lista = normalizarLecciones(res.data.data.lecciones);
                setItems(lista);
                sincronizarPadre(lista);
                toast.success(editingId ? "Lección actualizada." : "Lección agregada.");
                setShowForm(false);
                setEditingId(null);
                setForm(FORM_VACIO());
            }
        } catch (e) {
            toast.error(
                e.response && e.response.data && e.response.data.message ? e.response.data.message : e.message
            );
        } finally {
            setSaving(false);
        }
    };

    const eliminar = async () => {
        if (!deleteTarget) return;
        setSaving(true);
        try {
            const res = await deleteLeccionProyecto(proyectoId, deleteTarget.id);
            if (res.data && res.data.success) {
                const lista = normalizarLecciones(res.data.data.lecciones);
                setItems(lista);
                sincronizarPadre(lista);
                toast.success("Lección eliminada.");
            }
        } catch (e) {
            toast.error(
                e.response && e.response.data && e.response.data.message ? e.response.data.message : e.message
            );
        } finally {
            setSaving(false);
            setDeleteTarget(null);
        }
    };

    const resumen = useMemo(() => {
        const porTipo = { positiva: 0, negativa: 0, mejora: 0 };
        items.forEach((l) => {
            if (porTipo[l.tipo] !== undefined) porTipo[l.tipo] += 1;
        });
        return { total: items.length, porTipo };
    }, [items]);

    return (
        <div className="lecciones-aprendidas-container">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h5 className="mb-0" style={{ color: "#0056b3" }}>Lecciones Aprendidas</h5>
                    <small className="text-muted">
                        Documente experiencias en cualquier momento del proyecto. Se reflejan en las estadísticas del programa.
                    </small>
                </div>
                {puedeGestionar && (
                    <Button variant="success" onClick={abrirNueva}>
                        <i className="bi bi-plus-lg mr-1" /> Agregar lección
                    </Button>
                )}
            </div>

            <div className="mb-3">
                <Badge variant="secondary" className="mr-2">Total: {resumen.total}</Badge>
                <Badge variant="success" className="mr-2">Positivas: {resumen.porTipo.positiva}</Badge>
                <Badge variant="danger" className="mr-2">Negativas: {resumen.porTipo.negativa}</Badge>
                <Badge variant="info">Mejora: {resumen.porTipo.mejora}</Badge>
            </div>

            {loading ? (
                <p className="text-muted">Cargando lecciones…</p>
            ) : items.length === 0 ? (
                <Card className="border-0 shadow-sm">
                    <Card.Body className="text-center text-muted py-5">
                        Aún no hay lecciones registradas para este proyecto.
                        {puedeGestionar && " Usá «Agregar lección» para comenzar."}
                    </Card.Body>
                </Card>
            ) : (
                <Row>
                    {items.map((l) => {
                        const tipo = TIPOS_LECCION[l.tipo] || TIPOS_LECCION.mejora;
                        return (
                            <Col xs={12} md={6} lg={4} key={l.id} className="mb-3">
                                <Card className="h-100 shadow-sm border-0">
                                    <Card.Body>
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                            <strong className="text-primary">{l.titulo || "(Sin título)"}</strong>
                                            <Badge variant={tipo.variant}>{tipo.label}</Badge>
                                        </div>
                                        <div className="mb-2">
                                            <Badge variant="light" className="border text-dark mr-2">{l.categoria}</Badge>
                                            {l.fecha && (
                                                <small className="text-muted">{moment(l.fecha).format("DD/MM/YYYY")}</small>
                                            )}
                                        </div>
                                        <p className="mb-2" style={{ whiteSpace: "pre-wrap" }}>{l.descripcion}</p>
                                        {l.autorNombre && (
                                            <small className="text-muted d-block">Autor: {l.autorNombre}</small>
                                        )}
                                        {puedeGestionar && (
                                            <div className="mt-3 d-flex">
                                                <Button size="sm" variant="outline-primary" className="mr-2" onClick={() => abrirEditar(l)}>
                                                    <i className="bi bi-pencil" /> Editar
                                                </Button>
                                                <Button size="sm" variant="outline-danger" onClick={() => setDeleteTarget(l)}>
                                                    <i className="bi bi-trash" /> Eliminar
                                                </Button>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
            )}

            {/* Formulario (alta/edición) */}
            <Modal show={showForm} onHide={() => setShowForm(false)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{editingId ? "Editar lección" : "Nueva lección aprendida"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group>
                            <Form.Label>Título</Form.Label>
                            <Form.Control
                                value={form.titulo}
                                onChange={(e) => setForm((p) => ({ ...p, titulo: e.target.value }))}
                                placeholder="Resumen breve de la lección"
                            />
                        </Form.Group>
                        <Row>
                            <Col md={4}>
                                <Form.Group className="mt-3">
                                    <Form.Label>Categoría</Form.Label>
                                    <Form.Control
                                        as="select"
                                        value={form.categoria}
                                        onChange={(e) => setForm((p) => ({ ...p, categoria: e.target.value }))}
                                    >
                                        {CATEGORIAS_LECCION.map((c) => (
                                            <option key={c} value={c}>{c}</option>
                                        ))}
                                    </Form.Control>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mt-3">
                                    <Form.Label>Tipo</Form.Label>
                                    <Form.Control
                                        as="select"
                                        value={form.tipo}
                                        onChange={(e) => setForm((p) => ({ ...p, tipo: e.target.value }))}
                                    >
                                        <option value="positiva">Positiva</option>
                                        <option value="negativa">Negativa</option>
                                        <option value="mejora">Mejora</option>
                                    </Form.Control>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mt-3">
                                    <Form.Label>Fecha</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={form.fecha}
                                        onChange={(e) => setForm((p) => ({ ...p, fecha: e.target.value }))}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mt-3">
                            <Form.Label>Descripción</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={5}
                                value={form.descripcion}
                                onChange={(e) => setForm((p) => ({ ...p, descripcion: e.target.value }))}
                                placeholder="Detalle de la experiencia, contexto e impacto…"
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowForm(false)} disabled={saving}>Cancelar</Button>
                    <Button variant="primary" onClick={guardar} disabled={saving}>
                        {saving ? "Guardando…" : "Guardar"}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Confirmación de borrado */}
            <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Eliminar lección</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    ¿Está seguro que desea eliminar la lección <strong>{deleteTarget?.titulo || "(Sin título)"}</strong>?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={saving}>Cancelar</Button>
                    <Button variant="danger" onClick={eliminar} disabled={saving}>Eliminar</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default LeccionesAprendidas;
